// src/utils/localDate.ts
// Timezone-safe local-date helpers — NEVER use `new Date().toISOString().split('T')[0]` // perf-sweep-ignore: documents the anti-pattern this module replaces
// for user-facing dates: toISOString() converts to UTC, so a midnight-CEST moment
// (22:00/23:00 UTC the previous day) renders as YESTERDAY. That is the P.9 off-by-one
// bug class from the 2026-09-19 performance audit.
//
// Pattern source: src/features/haustechnik/hooks/useLiveStayReservations.ts
// (the already-fixed reference implementation).

/**
 * Formats a Date (or date-like value) as a LOCAL calendar date string `YYYY-MM-DD`.
 * No UTC conversion — the day boundary follows the user's local timezone.
 *
 * Accepts the same inputs `new Date()` accepts; returns '' for invalid values so
 * callers can fall back to their existing empty-string conventions.
 */
export function toLocalIso(input: Date | number | string = new Date()): string {
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Local calendar date shifted by N days (negative = past, positive = future).
 * Replaces the common `d.setDate(d.getDate() - n); d.toISOString().split('T')[0]` // perf-sweep-ignore: documents the anti-pattern this module replaces
 * idiom, which ALSO suffers the UTC-shift bug.
 */
export function toLocalIsoOffset(input: Date = new Date(), offsetDays = 0): string {
  const d = new Date(input.getTime());
  d.setDate(d.getDate() + offsetDays);
  return toLocalIso(d);
}

/** Today in local calendar time. Sugar for toLocalIso(new Date()). */
export function localToday(): string {
  return toLocalIso(new Date());
}
